import { useCallback, useState } from 'react';

import { useUser } from '@clerk/react';
import {
  Address,
  EmailProvider,
  EmailRequestType,
  LabelId,
  UnifiedSendEmailRequest,
  UserCommunication,
} from '@this+that/models';
import log from 'loglevel';

import { useActiveInboxEntriesSlice } from './useActiveInboxEntriesSlice';
import { useUpdateInboxEntryCollections } from '../../services/inboxEntries.service';
import { useIntegrationsStore, useUserCommunicationsStore } from '../../store';
import {
  emptyToUndefined,
  getAddressesByType,
} from '../../utils/getAddressesByType';
import { useCreateUserCommunication } from '../UserCommunications';
import { FileAttachment } from '../useFileAttachments';

const MESSAGE_PREVIEW_MAX_LENGTH = 200;

export const useInboxEntryEmailCreation = (onClose: () => void) => {
  const { updateInboxEntryCollections } = useUpdateInboxEntryCollections();
  const { insertUserCommunication } = useUserCommunicationsStore();
  const { inboxEntries, saveDetailedInboxEntry } = useActiveInboxEntriesSlice();
  const { handleCreateUserCommunication } = useCreateUserCommunication();
  const { currentIntegrationAccount } = useIntegrationsStore();
  const { user } = useUser();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleEmailCreation = useCallback(
    async (
      content: string,
      subject: string,
      recipients: Address[],
      inboxEntryId: string,
      attachments?: FileAttachment[],
      onSent?: () => void,
    ): Promise<boolean> => {
      setIsLoading(true);
      let isSent = false;
      const currentEntry = inboxEntries.find(
        (entry) => entry.id === inboxEntryId,
      );
      try {
        if (!currentEntry) {
          log.error('InboxEntry not found for email creation');
          return false;
        }

        if (!currentIntegrationAccount) {
          log.error('No integration mail found');
          return false;
        }

        const { platform, displayName } = currentIntegrationAccount as {
          platform: EmailProvider;
          displayName: string;
        };

        const {
          to: toAddresses = [],
          cc: ccAddresses = [],
          bcc: bccAddresses = [],
        } = getAddressesByType(recipients);

        const payload: UnifiedSendEmailRequest = {
          providerAccount: { provider: platform, accountId: displayName },
          reqType: EmailRequestType.CREATE,
          body: content,
          subject,
          toAddresses,
          ccAddresses: emptyToUndefined(ccAddresses),
          bccAddresses: emptyToUndefined(bccAddresses),
          attachments,
        };

        const response = await handleCreateUserCommunication(payload);

        if (response?.message?.messageId) {
          isSent = true;
          onSent?.();
          const messageId = response.message.messageId;

          const now = Date.now();
          const userEmail = currentIntegrationAccount.displayName;
          const userName = user?.fullName || user?.firstName || '';
          const userId = user?.id || '';

          const newUserCommunication: UserCommunication = {
            messageId,
            userId,
            userEmailAddress: userEmail,
            createdAt: now,
            author: userName,
            authorUserId: userId,
            authorEmail: userEmail,
            messagePreview: content.substring(0, MESSAGE_PREVIEW_MAX_LENGTH),
            messageRawText: content,
            messageText: content,
            recipients: recipients.map((addr) => ({
              userId: '',
              email: addr.value,
              name: addr.label
                ? addr.label.split('<')[0].trim()
                : addr.value?.includes('@')
                  ? ''
                  : addr.value,
              type: addr.type,
            })),
            subject,
            platform: platform,
            labelIds: [LabelId.SENT, LabelId.INBOX],
          };

          insertUserCommunication(newUserCommunication);

          const updatedCommunications = [
            newUserCommunication,
            ...(currentEntry.communications || []),
          ];
          const updatedCommunicationIds = [
            messageId,
            ...(currentEntry.communicationIds || []),
          ];

          const updatedEntry = {
            ...currentEntry,
            communications: updatedCommunications,
            communicationIds: updatedCommunicationIds,
          };

          saveDetailedInboxEntry(updatedEntry);

          await updateInboxEntryCollections(inboxEntryId, {
            communicationIds: {
              add: [messageId],
            },
          });
        }
        return isSent;
      } catch (error) {
        log.error('Failed to create email for inbox entry:', error);
        throw error;
      } finally {
        setIsLoading(false);
        if (isSent) {
          onClose();
        }
      }
    },
    [
      currentIntegrationAccount,
      handleCreateUserCommunication,
      insertUserCommunication,
      onClose,
      saveDetailedInboxEntry,
      updateInboxEntryCollections,
      user,
    ],
  );

  return {
    handleEmailCreation,
    isLoading,
  };
};
